'use client';

/**
 * AutoBreadcrumbSchema - pathname-aware BreadcrumbList JSON-LD.
 *
 * Drops into the root layout. Reads the current pathname, builds a
 * human-readable breadcrumb chain from the URL segments, and emits
 * Schema.org BreadcrumbList JSON-LD for Google SERP rich breadcrumbs.
 *
 * No UI changes. Each page can still ship its own visible breadcrumbs
 * (and its own JSON-LD if it wants - Google de-dupes by URL).
 *
 * Hard rule: every breadcrumb name is derived from the URL segments
 * (slug → Title Case). No fabricated names. No data-source lookups.
 */

import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { jsonLd } from '@/lib/seo/json-ld';

const BASE_URL = 'https://www.scriptunlock.com';

// Skip emitting breadcrumb schema on these surfaces (they're either single-
// breadcrumb pages, gated app surfaces, or non-content routes).
const SKIP_PREFIXES = [
  '/api',
  '/auth',
  '/dashboard',
  '/admin',
  '/_next',
  '/account',
  '/settings',
  '/onboarding',
  '/login',
  '/signup',
  '/sign-in',
  '/sign-up',
  '/sso',
];

// Short slugs we want preserved as-is (rather than title-cased to "Hrt")
const PRESERVE_SLUGS: Record<string, string> = {
  hrt: 'HRT',
  trt: 'TRT',
  glp1: 'GLP-1',
  'glp-1': 'GLP-1',
  pbm: 'PBM',
  hsa: 'HSA',
  fsa: 'FSA',
  fda: 'FDA',
  faq: 'FAQ',
  faqs: 'FAQs',
  rx: 'Rx',
  usa: 'USA',
  us: 'US',
  uk: 'UK',
  au: 'AU',
  ca: 'CA',
  goodrx: 'GoodRx',
  scriptunlock: 'ScriptUnlock',
  cvs: 'CVS',
  // Old URLs kept for links and rankings; the pages say "quotes" (2026-10-25).
  'how-pharmacy-bidding-works': 'How Pharmacy Quotes Work',
  'no-bids-help': 'No Quotes Yet',
};

function titleCaseSlug(slug: string): string {
  const lower = slug.toLowerCase();
  if (PRESERVE_SLUGS[lower]) return PRESERVE_SLUGS[lower];
  return slug
    .split('-')
    .map((word) => {
      const w = word.toLowerCase();
      if (PRESERVE_SLUGS[w]) return PRESERVE_SLUGS[w];
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(' ');
}

export default function AutoBreadcrumbSchema() {
  const pathname = usePathname();

  const schema = useMemo(() => {
    if (!pathname || pathname === '/') return null;
    if (SKIP_PREFIXES.some((p) => pathname.startsWith(p))) return null;

    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return null;

    // Build crumbs cumulatively
    const crumbs = [{ name: 'Home', url: `${BASE_URL}/` }];
    let acc = '';
    segments.forEach((seg, i) => {
      acc += `/${seg}`;
      const isLast = i === segments.length - 1;
      crumbs.push({
        name: titleCaseSlug(decodeURIComponent(seg)),
        // Per Google: omit `item` on the last crumb (current page).
        // But schema needs URL; pass and let Google handle.
        url: `${BASE_URL}${acc}`,
        ...(isLast ? { __isLast: true as const } : {}),
      } as { name: string; url: string });
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: c.name,
        item: c.url,
      })),
    };
  }, [pathname]);

  if (!schema) return null;

  return (
    <script
      type="application/ld+json"
      // suppressHydrationWarning because pathname renders fine but Google
      // only needs the static markup - the schema is identical SSR/CSR.
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: jsonLd(schema) }}
    />
  );
}
